import './Guides.css';

import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ApiError } from '../api/client';
import { GuideIndex } from '../api/guides';
import { useGuide, useGuides } from '../query/guides';
import Markdown from './Markdown';

const asDate = (iso: string) => (iso ? iso.slice(0, 10) : '');

function Crumbs({ apiId, index, title }: { apiId: string; index?: GuideIndex; title?: string }) {
  return (
    <nav className="guides-crumbs">
      <Link to={`/apis/${apiId}`}>{index?.displayName || apiId}</Link>
      <span>/</span>
      {title ? <Link to={`/apis/${apiId}/guides`}>Guides</Link> : <span>Guides</span>}
      {title && (
        <>
          <span>/</span>
          <span>{title}</span>
        </>
      )}
    </nav>
  );
}

// ── Index ────────────────────────────────────────────────────────────────────

function GuidesIndex({ apiId }: { apiId: string }) {
  const query = useGuides(apiId);
  const index = query.data;

  return (
    <div className="guides">
      <Crumbs apiId={apiId} index={index} />
      <h1 className="guides-title">
        {index?.displayName ? `${index.displayName} guides` : 'Guides'}
      </h1>

      {query.isPending && <p className="guides-empty">Loading guides…</p>}
      {query.isError && <div className="guides-notice">Could not load the guides.</div>}

      {index && index.guides.length === 0 && (
        <p className="guides-empty">This service has published no guides.</p>
      )}

      {index && index.guides.length > 0 && (
        <>
          <p className="guides-meta">
            {index.guides.length} guide{index.guides.length === 1 ? '' : 's'}
            {index.updatedAt && ` · updated ${asDate(index.updatedAt)}`}
          </p>
          <div className="guides-list">
            {index.guides.map((guide) => (
              <Link className="guides-card" key={guide.id} to={`/apis/${apiId}/guides/${guide.id}`}>
                <p className="guides-card-title">{guide.title}</p>
                {guide.summary && <p className="guides-card-summary">{guide.summary}</p>}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── One guide ────────────────────────────────────────────────────────────────

function Pager({ apiId, index, guideId }: { apiId: string; index?: GuideIndex; guideId: string }) {
  const guides = index?.guides ?? [];
  const at = guides.findIndex((g) => g.id === guideId);
  const previous = at > 0 ? guides[at - 1] : null;
  const next = at >= 0 && at < guides.length - 1 ? guides[at + 1] : null;
  if (!previous && !next) {
    return null;
  }
  return (
    <nav aria-label="Neighbouring guides" className="guides-pager">
      {previous && (
        <Link className="previous" to={`/apis/${apiId}/guides/${previous.id}`}>
          <span className="guides-pager-label">Previous</span>
          {previous.title}
        </Link>
      )}
      {next && (
        <Link className="next" to={`/apis/${apiId}/guides/${next.id}`}>
          <span className="guides-pager-label">Next</span>
          {next.title}
        </Link>
      )}
    </nav>
  );
}

function Guide({ apiId, guideId }: { apiId: string; guideId: string }) {
  const indexQuery = useGuides(apiId);
  const query = useGuide(apiId, guideId);
  const doc = query.data;
  const missing = query.isError && query.error instanceof ApiError && query.error.notFound;

  // A deep link with a fragment lands before the body has rendered, so the
  // browser finds no element to scroll to; do it once the guide is in.
  const { hash } = useLocation();
  useEffect(() => {
    if (doc && hash) {
      document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView();
    }
  }, [doc, hash]);

  return (
    <div className="guides">
      <Crumbs apiId={apiId} index={indexQuery.data} title={doc?.guide.title ?? guideId} />

      {query.isPending && <p className="guides-empty">Loading guide…</p>}

      {missing && (
        <>
          <h1 className="guides-title">No such guide</h1>
          <div className="guides-notice">
            This service publishes no guide called <code>{guideId}</code>. The link is most likely
            out of date.
          </div>
          <p className="guides-meta">
            <Link to={`/apis/${apiId}/guides`}>All guides</Link>
          </p>
        </>
      )}
      {query.isError && !missing && <div className="guides-notice">Could not load this guide.</div>}

      {doc && (
        <article>
          <h1 className="guides-title">{doc.guide.title}</h1>
          {doc.guide.summary && <p className="guides-lede">{doc.guide.summary}</p>}
          {/* Body headings start at ## — rendered as h2 under the title above. */}
          <Markdown className="md--article" headingOffset={0} source={doc.guide.body} />
          <Pager apiId={apiId} guideId={guideId} index={indexQuery.data} />
          <p className="guides-footer">
            Written and published by {doc.displayName || apiId}
            {doc.updatedAt && ` · last updated ${asDate(doc.updatedAt)}`}.
          </p>
        </article>
      )}
    </div>
  );
}

// ── Container ────────────────────────────────────────────────────────────────

interface Props {
  apiId: string;
  /** One guide's page; omit for the index. */
  guideId?: string;
}

/**
 * The guides section of a service's API reference: prose the service wrote for
 * integrators, pulled from the service's own published bundle. Unversioned and
 * public — see docs/service-guides.md.
 */
export default function GuideContent({ apiId, guideId }: Props) {
  return guideId ? <Guide apiId={apiId} guideId={guideId} /> : <GuidesIndex apiId={apiId} />;
}
